import type { FormProduct, FormVariant } from "@shared/types";
import type { WebStatus, Term } from "@shared/services";
import type { Product, Variant } from "@bcc/components/products/products-table";

/**
 * Default commission percentage applied to all products.
 *
 * Business Rule: 15% commission is taken from all product sales before payout to roasters.
 * This is a fixed rate applied universally across all products and roasters.
 *
 * Note: If commission rates need to vary by product, roaster, or over time,
 * this should be moved to API data and added to the FormVariant type.
 */
const DEFAULT_COMMISSION_PERCENTAGE = 15;

/**
 * Map WebStatus to BCC table status
 */
function mapWebStatusToStatus(
  webStatus?: WebStatus,
  variantStatus?: string,
): Variant["status"] {
  // Check for rejected status first
  if (variantStatus === "Rejected") {
    return "rejected";
  }

  if (!webStatus) {
    return "draft";
  }

  switch (webStatus) {
    case "Visible for Sale":
      return "published";
    case "Not Visible":
      return "draft";
    case "Out of Stock":
      return "out_of_stock";
    default:
      return "draft";
  }
}

/**
 * Map Term to BCC table termType
 */
function mapTermToTermType(term?: Term): Variant["termType"] {
  if (term === "Long") {
    return "long_term";
  }
  return "short_term";
}

/**
 * Parse price to number, handling string or number input
 */
function parsePrice(price?: string | number): number {
  if (price === undefined || price === null) {
    return 0;
  }
  if (typeof price === "number") {
    return price;
  }
  const parsed = parseFloat(price);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Calculate payout based on sale price and commission
 */
function calculatePayout(salePrice: number, commission: number): number {
  return Number((salePrice * (1 - commission / 100)).toFixed(2));
}

/**
 * Calculate margin based on sale price and purchase price
 */
function calculateMargin(salePrice: number, purchasePrice: number): number {
  if (salePrice === 0) {
    return 0;
  }
  return Number((((salePrice - purchasePrice) / salePrice) * 100).toFixed(2));
}

/**
 * Transform a FormVariant to BCC table Variant
 */
export function transformVariant(
  formVariant: FormVariant,
  index: number,
): Variant {
  const purchasePrice = parsePrice(formVariant.wholesalePrice);
  const salePrice = parsePrice(formVariant.price);
  const commission = DEFAULT_COMMISSION_PERCENTAGE;

  return {
    id: formVariant.id ?? `variant-${index}`,
    size: formVariant.bagSize ?? "12oz",
    sku: formVariant.id ?? `sku-${index}`,
    status: mapWebStatusToStatus(formVariant.webStatus, formVariant.status),
    subscribers: formVariant.activeSubscriberCount ?? 0,
    purchasePrice,
    salePrice,
    commission,
    payout: calculatePayout(salePrice, commission),
    margin: calculateMargin(salePrice, purchasePrice),
    weeklyLimit: formVariant.maxBagsShippedPerWeek ?? 0,
    termType: mapTermToTermType(formVariant.term),
    termEndDate: formVariant.endDate,
    grindMappings: formVariant.grind ?? [],
    stock: formVariant.inventoryQuantity ?? 0,
  };
}

/**
 * Determine product source based on variants
 * If any variant has a localId, it's from Shopify
 */
function determineSource(formProduct: FormProduct): Product["source"] {
  return formProduct.localId ||
    formProduct.variants?.some((variant) => variant.isImportedLocalVariant)
    ? "shopify"
    : "manual";
}

/**
 * Group FormVariants by bag size, combining grind mappings within each group.
 *
 * The source data may have one variant per bag-size×grind combination. The
 * table wants one row per bag size with all grinds listed in a single cell.
 */
function groupVariantsByBagSize(
  formVariants: FormVariant[],
): FormVariant[] {
  const groups = new Map<string, FormVariant[]>();
  for (const v of formVariants) {
    const key = v.bagSize ?? "default";
    const existing = groups.get(key);
    if (existing) {
      existing.push(v);
    } else {
      groups.set(key, [v]);
    }
  }

  return Array.from(groups.values()).map((group) => {
    const primary = group[0]!;
    const allGrinds = group.flatMap((v) => v.grind ?? []);
    return { ...primary, grind: allGrinds };
  });
}

/**
 * Transform a FormProduct to BCC table Product
 */
export function transformProduct(
  formProduct: FormProduct,
  index: number,
): Product {
  const parsedId =
    typeof formProduct.id === "string" && formProduct.id.trim() !== ""
      ? Number(formProduct.id)
      : Number.NaN;
  const numericId = Number.isNaN(parsedId) ? index + 1 : parsedId;

  const groupedVariants = groupVariantsByBagSize(formProduct.variants ?? []);

  return {
    id: numericId,
    name: formProduct.title ?? "Untitled Product",
    sku: formProduct.id ?? `product-${index}`,
    category: formProduct.productType ?? "Coffee",
    imageUrl: formProduct.images?.[0]?.src ?? "",
    description: formProduct.description ?? "",
    source: determineSource(formProduct),
    variants: groupedVariants.map((variant, variantIndex) =>
      transformVariant(variant, variantIndex),
    ),
  };
}

/**
 * Options for transformFormProducts
 */
export interface TransformOptions {
  /** Attach connection data for UI display */
  includeConnectionInfo?: boolean;
}

/**
 * Connection status information for a product
 */
export interface ProductConnectionStatus {
  hasConnections: boolean;
  connectionCount: number;
}

/**
 * Check if a FormProduct has active connections
 */
export function hasActiveConnections(formProduct: FormProduct): boolean {
  return (
    Array.isArray(formProduct.connections) && formProduct.connections.length > 0
  );
}

/**
 * Get connection status for a FormProduct
 */
export function getProductConnectionStatus(
  formProduct: FormProduct,
): ProductConnectionStatus {
  const connections = formProduct.connections ?? [];
  return {
    hasConnections: connections.length > 0,
    connectionCount: connections.length,
  };
}

/**
 * Transform an array of FormProducts to BCC table Products
 */
export function transformFormProducts(
  formProducts: FormProduct[] | undefined | null,
  _options?: TransformOptions,
): Product[] {
  if (!formProducts || !Array.isArray(formProducts)) {
    return [];
  }

  return formProducts
    .filter((product): product is FormProduct => product != null)
    .map((product, index) => transformProduct(product, index));
}
